import React, { useState } from 'react'
import '../../styles/analytics/CatchUpHome.css'
import { ReactComponent as SettingsIcon } from '../../assets/svg/cong.svg'
import InfiniteScroll from 'react-infinite-scroll-component'
import { ReactComponent as DropdownTransparent } from '../../assets/svg/DropdownTransparent.svg'
import { ReactComponent as SeenFeedbackIcon } from '../../assets/svg/SeenFeedbackIcon.svg'
import { ReactComponent as Magnify } from '../../assets/svg/search-magnify-icon.svg'
import { emotionLabel, notify, timeSince, usePageTitle } from '../../utils/util'
import Loading from '../Loading'
import { ReactComponent as Filter } from '../../assets/svg/filter.svg'
import { emotions_heads } from '../../constants/Constants'
import { getCatchUpsAsync, sendSeenFeedbackComment } from '../../server/server'
import { useEffect } from 'react'
import FeedBackModal from '../modals/FeedBackModal/FeedBackModal'
import { useUser } from '../../auth'
import { useTranslation } from 'react-i18next'
import { CatchUpItemSkeleton } from './skeleton/CatchUpSkeleton'
import ClassroomFilterDropdown from '../classroom/ClassroomFilterDropdown'
import { oneOfCodec, useUrlState } from '../../hooks/useUrlState'
import { ANALYTICS_EVENTS } from '../../analytics/events'
import useTrackedSurface from '../../analytics/useTrackedSurface'

const MODES = ['unseen', 'archive']

const CatchUpHome = () => {
    usePageTitle('Upstrive Education - Catch-up')
    const [showTargetGroup, setShowTargetGroup] = useState(false)
    const [selectedStudent, setSelectedSTudent] = useState({})
    // Not a filter: this is only the day shown inside the feedback modal.
    const [selectedDay, setSelectedDay] = useState(new Date().getTime())
    const [openStudentFeedback, setOpenStudentFeedback] = useState(false)
    // Scalar with an 'all' sentinel, and not validated against the year/class
    // options: those only arrive with the list response, so there is nothing to
    // check against at mount. A bogus year simply returns an empty list.
    const [selectedYearFilter, setSelectedYearFilter] = useUrlState('year', 'all')
    const [selectedClassFilter, setSelectedClassFilter] = useUrlState('class', 'all')
    const [selectedMode, setSelectedMode] = useUrlState('tab', 'unseen', oneOfCodec(MODES))
    const [filterYears, setFilterYears] = useState([])
    const [selectedYearList, setSelectedYearList] = useState(null)
    const [classesOfYear, setClassesOfYear] = useState({})
    const [loadingFilter, setLoadingFilter] = useState(false)
    const [searchText, setSearchText] = useUrlState('search', '', { debounce: 600 })
    const [catchUps, setCatchUps] = useState([])
    const [loadingMore, setLoadingMore] = useState(false)
    const [initialLoading, setInitialLoading] = useState(true)
    const { t, locale } = useTranslation()

    const { user } = useUser()
    useTrackedSurface(selectedMode === 'archive' ? ANALYTICS_EVENTS.CATCH_UP_ARCHIVE_VIEWED : ANALYTICS_EVENTS.CATCH_UP_UNSEEN_VIEWED)

    // useEffect(() => {
    // 	getCatchUps(0);
    // }, []);

    const getCatchUps = async (numberOfUsers, query = {}, searchText = '') => {
        const data = {
            number_of_students: numberOfUsers,
            query,
            search_text: searchText,
            mode: selectedMode,
        }
        const resp = await getCatchUpsAsync(data)
        if (resp.status === 200) {
            const data = resp.data
            const previousList = numberOfUsers === 0 ? [] : catchUps
            setCatchUps([...previousList, ...data.data])
            setFilterYears(Object.keys(data['filter']).filter((e) => e !== 'all'))
            setClassesOfYear(data['filter'])
            setSelectedYearList(data['filter'][selectedYearFilter])
            setInitialLoading(false)
            setTimeout(() => {
                setLoadingMore(false)
            }, 200)
        }
    }

    const selectYearFilter = (year) => {
        setSelectedClassFilter('all')
        setSelectedYearFilter(year)
        setSelectedYearList(classesOfYear[year])
    }

    const selectAllClasses = () => {
        setSelectedClassFilter('all')
    }
    const setQuery = () => {
        let data = {}
        if (selectedYearFilter != 'all') {
            data['year'] = selectedYearFilter
        } else {
            delete data['year']
        }
        if (selectedClassFilter != 'all') {
            data['class'] = selectedClassFilter
        } else {
            delete data['class']
        }
        if (data['year'] === '' || data['class'] === '') {
            notify(t('filterIsNotSet'), 'warning')
            return
        } else return data
    }
    // Defaults to the live searchText: the filter dropdown's Apply calls this
    // with no argument, which previously reset the search to '' behind the
    // user's back while the search box still showed their text.
    const applyFilter = async (text = searchText) => {
        let data = setQuery()
        setLoadingFilter(true)
        await getCatchUps(0, data, text)
        setLoadingFilter(false)
    }

    const loadMore = () => {
        if (!loadingMore) {
            setLoadingMore(true)
            let data = setQuery()
            getCatchUps(catchUps.length, data, searchText)
        }
    }

    const closeStudentFeedback = () => {
        if (selectedMode == 'unseen') setCatchUps(catchUps.filter((e) => e.id != selectedStudent?.feedback?.id))
        setOpenStudentFeedback(false)
    }

    const markAsSeen = async (feedback_id) => {
        const data = {
            user_id: user['id'],
            feedback_id: feedback_id,
        }
        const resp = await sendSeenFeedbackComment(data)
        if (resp.status === 200) {
            const data = resp.data
            notify(t('commentHasBeenSeen'))
            setCatchUps(catchUps.filter((e) => e.id !== feedback_id))
        } else {
            console.log('Send Seen error: ', resp)
        }
    }

    const openFeedback = (feedback) => {
        setOpenStudentFeedback(true)
        setSelectedSTudent({ feedback: feedback, user: feedback.user })
        setSelectedDay(feedback.created_date)
    }

    // Deliberately unguarded: firing on mount is this screen's only initial load.
    useEffect(() => {
        const timeout = setTimeout(() => {
            applyFilter(searchText)
        }, 600)
        return () => clearTimeout(timeout)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText, selectedMode])

    const clickUnseen = () => {
        setInitialLoading(true)
        setSelectedMode('unseen')
    }
    const clickArchive = () => {
        setInitialLoading(true)
        setSelectedMode('archive')
    }

    return (
        <div className="catch-up-container bg-[#ECEBF8]" id="scroll-inf-target">
            <div className="catch-up-inner-container">
                <div className="catch-up-header-container">
                    <div className="catch-up-titles">
                        <h1 className="catch-up-title">{t('catchUp')} </h1>
                        <h2 className="catch-up-subtitle">{t('catchUpOnAnyStudentCommentsAttachedTo')}</h2>
                    </div>
                    {/* <button
						className='settings-button'
						onClick={() => {
							getCatchUps(0);
						}}
					>
						<SettingsIcon />
					</button> */}
                </div>
                <div className="flex flex-row w-full mt-[20px]">
                    <button
                        className={`catch-up-mode-button mr-[20px] ${selectedMode == 'unseen' ? 'bg-[#5E639B] text-[#FFFFFF]' : 'bg-[#FFFFFF] text-[#9392AD]'}`}
                        onClick={clickUnseen}
                    >
                        {t('unseen')}
                    </button>
                    <button className={`catch-up-mode-button ${selectedMode == 'archive' ? 'bg-[#5E639B] text-[#FFFFFF]' : 'bg-[#FFFFFF] text-[#9392AD]'}`} onClick={clickArchive}>
                        {t('archive')}
                    </button>
                </div>
                <div className="flex flex-row items-center my-[20px] w-full">
                    <div className="catch-up-search-container">
                        <input type="text" className="search flex-1" id="students-search-input" placeholder={t('searchStudents')} onChange={(e) => setSearchText(e.target.value)} />
                        <Magnify />
                    </div>
                    <ClassroomFilterDropdown
                        title={t('filter')}
                        id="catchup-filter-button"
                        applyButtonId="catchup-apply-filter-button"
                        filterYears={filterYears}
                        selectedYearFilter={selectedYearFilter}
                        selectYearFilter={selectYearFilter}
                        selectedClassFilter={selectedClassFilter}
                        selectedYearList={selectedYearList}
                        selectAllClasses={selectAllClasses}
                        selectClass={setSelectedClassFilter}
                        applyFilter={applyFilter}
                        loadingFilter={loadingFilter}
                        hideStaffButton={true}
                        height={50}
                    />
                </div>

                {!initialLoading && (
                    <div className="catch-up-list">
                        <InfiniteScroll
                            key={`${selectedMode}-${selectedYearFilter}-${selectedClassFilter}-${searchText}`} //if loaded list empty restart InfiniteScroll
                            dataLength={catchUps?.length}
                            next={loadMore}
                            hasMore={true}
                            scrollableTarget="scroll-inf-target"
                            style={{ overflowX: 'hidden', overflowY: 'auto' }}
                        >
                            {catchUps.map((feedback, i) => {
                                const timeMessage =
                                    selectedMode == 'unseen'
                                        ? t('Left 6 days ago', { time: timeSince(feedback.created_date) })
                                        : !!feedback?.seenDate
                                          ? t('Seen 6 days ago', { time: timeSince(feedback.seenDate) })
                                          : ''
                                return (
                                    <button
                                        className="catch-up-button"
                                        key={feedback.id}
                                        onClick={() => {
                                            openFeedback(feedback)
                                        }}
                                        id={`catch-up-button-${i}`}
                                    >
                                        <div className="flex flex-row items-center">
                                            <img src={emotions_heads[feedback.emotion]} alt={emotionLabel(t, feedback.emotion)} width={35} height={35} />
                                            <p className="catch-up-student-name">
                                                {feedback?.user?.first_name} {feedback?.user?.last_name}
                                            </p>
                                            <p className="catch-up-student-class" id={`catch-up-student-class-${i}`}>
                                                {feedback?.user?.classesName}
                                            </p>
                                            <span className="flex-1"></span>
                                            <span className="catch-up-time">{timeMessage}</span>
                                        </div>
                                        <div className="flex flex-row items-end">
                                            <p className="catch-up-message">“{feedback?.message}”</p>
                                            {selectedMode == 'unseen' ? (
                                                <button
                                                    className="mark-as-seen-button"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        markAsSeen(feedback.id)
                                                    }}
                                                    id={`mark-as-seen-button-${i}`}
                                                >
                                                    <SeenFeedbackIcon className="seen-feedback-icon" />
                                                    <span className="ml-[7px]">{t('markAsSeen')} </span>
                                                </button>
                                            ) : (
                                                <div className="w-[30px] h-[30px] flex items-center justify-center bg-deepPurple rounded-full ">
                                                    <SeenFeedbackIcon fill="#FDDF6D" />
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                )
                            })}
                            {loadingMore && (
                                <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                                    {[...Array(4)].map((_, idx) => (
                                        <CatchUpItemSkeleton key={idx} />
                                    ))}
                                </div>
                            )}
                        </InfiniteScroll>
                    </div>
                )}
                {initialLoading && (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                        {[...Array(4)].map((_, idx) => (
                            <CatchUpItemSkeleton key={idx} />
                        ))}
                    </div>
                )}
            </div>
            <FeedBackModal modalIsOpen={openStudentFeedback} closeModal={closeStudentFeedback} student={selectedStudent} day={selectedDay} analyticsSource="catch_up" />
        </div>
    )
}

export default CatchUpHome
